// "Pick a wave" screen. Polls /picking/selector every 8s while
// mounted so counts, segment-badge counts, and paused waves stay
// fresh.
//
// Layout:
//   - Title row with Low/High tier toggle and back button
//   - Delivery type filter: All | ⚡ Express | 🚚 Standard
//   - Age filter: All | 1 | 2 | 2+
//   - Featured Pick & Sort tile (full-width orange gradient)
//   - Standard Single + Large tiles below in a 2-column grid
//
// Five independent inputs:
//   - tier: which floor of locations the allocator can pull from
//   - deliveryFilter: which delivery_type values count
//   - ageFilter: which order-age buckets count
//   - channelFilter: which sales_channel values count
//   - dueFilter: only orders that must ship today (clock toggle — shown
//     only after the 13:00 London dispatch cut-off; rule in dispatchDue.ts)
// Tile counts honour every active filter at once (intersection).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Feather, Layers, Package2, ArrowLeft, ChevronRight, ListChecks, Sparkles, Grid3x3, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import PausedWavesList, { type PausedWave } from "./PausedWavesList";
import QueueOrderList, { type SpecialQueue } from "./QueueOrderList";
import LargeAnchorScan from "./LargeAnchorScan";
import { startLargeWaveAt } from "./startLargeWave";
import { PICKING_POLL_MS } from "./constants";
// The selector URL, the wave-size rule, the response shapes and the
// starting overlay are shared with the "Which wave?" page
// (/picking/choose) so both screens poll and start identically; the
// filter state + its controls come from SelectorFilters so the two pages
// render the same filter UI above their cards.
import {
    buildSelectorUrl, persistStoredWaveSize, readStoredWaveSize, resolveWaveSize, WaveStartingOverlay,
    advisorPickKey, advisorStartMeta, readSuggestedOnly, persistSuggestedOnly,
    readStoredPair, persistStoredPair,
    type AdvisorInfo, type PasLargeInfo, type PutWallInfo, type SortStation, type PairingInfo,
} from "./selectorShared";
import {
    usePickFilters, TierToggle, SelectorFilterRows, EMPTY_AGE_COUNTS, EMPTY_DELIVERY_COUNTS,
    type AgeCounts, type ChannelCount, type DeliveryCounts,
} from "./SelectorFilters";

type PickType = "light" | "pick_and_sort" | "large";

interface SelectorResponse {
    counts: Record<PickType, number>;
    // Companion ORDER counts per tile. `large` is the whole filtered
    // pool ("N items / M orders" — Large is one order per wave);
    // `pick_and_sort` is the order count of the NEXT wave, the same
    // selection counts.pick_and_sort sums items over. Optional: an
    // older BE omits it and the tiles fall back to the items-only layout.
    order_counts?: { large?: number; pick_and_sort?: number };
    // Everything ready for Pick & Sort under the active filters (not
    // just the next wave's cap). Optional: older BEs omit it.
    pool_counts?: { pick_and_sort?: { items: number; orders: number } };
    age_counts: AgeCounts;
    delivery_counts: DeliveryCounts;
    // Items due today / overdue on this tier, unfiltered by the other
    // selections (clock-toggle badge). Optional: older BEs omit it.
    due_count?: number;
    channel_counts?: Record<string, ChannelCount>;
    special_queues?: SpecialQueue[];
    paused: PausedWave[];
    pick_and_sort_large?: PasLargeInfo;
    // Put wall (Pembroke): the tote-sized order pool that a put_wall
    // sort station takes. Absent (older BE) or enabled:false = feature
    // off, and the featured card starts a wave directly as before.
    put_wall?: PutWallInfo;
    // Pick Advisor (2026-10-02): the recommended pick for this queue, or
    // null/absent on an older BE. See selectorShared.AdvisorInfo.
    advisor?: AdvisorInfo | null;
    // Paired waves (migration 223): whether the warehouse allows them.
    // Absent (older BE) or enabled:false = no "Pick as a pair" toggle.
    pairing?: PairingInfo;
}

const iconFor = (kind: PickType, size: number) => {
    switch (kind) {
        case "light":         return <Feather size={size} strokeWidth={1.8} />;
        case "pick_and_sort": return <Layers size={size} strokeWidth={1.8} />;
        case "large":         return <Package2 size={size} strokeWidth={1.8} />;
    }
};

interface Props {
    onWaveStarted: () => void;
}

interface SpecialQueuesSectionProps {
    queues: SpecialQueue[] | null;
    busy?: boolean;
    onOpenQueue: (queue: SpecialQueue) => void;
}

export const SpecialQueuesSection = ({ queues, busy = false, onOpenQueue }: SpecialQueuesSectionProps) => {
    const { t } = useTranslation();
    // All three queues render whenever ANY has work (plan AC7) — an empty
    // queue stays visible-but-disabled so its (possibly renamed) name is
    // still discoverable from the picker screen. Only the whole section
    // hides when every queue is empty.
    const allQueues = queues ?? [];
    if (!allQueues.some(queue => queue.orders > 0)) return null;

    return (
        <section className="picking-special-queues" aria-labelledby="picking-special-queues-title">
            <h2 id="picking-special-queues-title" className="picking-special-queues-title">
                <ListChecks size={18} aria-hidden="true" />
                {t("picking_special_queues_title")}
            </h2>
            <ul className="picking-special-queues-list">
                {allQueues.map(queue => (
                    <li key={queue.id}>
                        <button
                            type="button"
                            className="picking-special-queue-row"
                            onClick={() => onOpenQueue(queue)}
                            disabled={busy || queue.orders === 0}
                        >
                            <span className="picking-special-queue-copy">
                                <strong>{queue.name}</strong>
                                <span>
                                    {t("picking_queue_orders", { count: String(queue.orders) })}
                                    {" · "}
                                    {t("picking_queue_items", { count: String(queue.items) })}
                                </span>
                            </span>
                            <ChevronRight size={20} aria-hidden="true" />
                        </button>
                    </li>
                ))}
            </ul>
        </section>
    );
};

const PickTypeSelector = ({ onWaveStarted }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // The five filter inputs + their persistence (shared with the
    // "Which wave?" page). `filters` is the effective set the poll
    // narrows to and a start forwards.
    const f = usePickFilters();
    const { filters, setChannelFilter } = f;
    const { tier, ageFilter, deliveryFilter, channelFilter, ageFrom, ageTo, dueFilter } = filters;
    const [dueCount, setDueCount] = useState<number>(0);
    const [counts, setCounts] = useState<Record<PickType, number>>({ light: 0, pick_and_sort: 0, large: 0 });
    // null = the BE didn't send order counts (older bundle) → the Large
    // tile renders its legacy items-only layout.
    const [largeOrderCount, setLargeOrderCount] = useState<number | null>(null);
    const [ageCounts, setAgeCounts] = useState<AgeCounts>(EMPTY_AGE_COUNTS);
    const [deliveryCounts, setDeliveryCounts] = useState<DeliveryCounts>(EMPTY_DELIVERY_COUNTS);
    const [channelCounts, setChannelCounts] = useState<Record<string, ChannelCount>>({});
    // null means an older backend omitted special_queues entirely. Keep that
    // distinct from an empty array so rolling deployments render no queue UI.
    const [specialQueues, setSpecialQueues] = useState<SpecialQueue[] | null>(null);
    const [selectedQueue, setSelectedQueue] = useState<SpecialQueue | null>(null);
    const [paused, setPaused] = useState<PausedWave[]>([]);
    const [pasLarge, setPasLarge] = useState<PasLargeInfo | null>(null);
    // Pick & Sort companions: orders in the next wave, and the whole
    // ready pool (items + orders). null = older BE, row hidden.
    const [pasOrderCount, setPasOrderCount] = useState<number | null>(null);
    const [pasPool, setPasPool] = useState<{ items: number; orders: number } | null>(null);
    // Put wall: null = feature off (older BE, setting off, or no live
    // put wall bay) → the featured card behaves exactly as before.
    const [putWall, setPutWall] = useState<PutWallInfo | null>(null);
    const putWallOn = putWall !== null;
    // Paired waves (Alex, 2026-10-16): the warehouse allows two pickers
    // on one Sorting / Putwall wave, and this picker's "Pick as a pair"
    // choice (per device). A paired start builds segment 1 for this
    // picker and leaves segment 2 on the Join card for anyone.
    const [pairing, setPairing] = useState<PairingInfo | null>(null);
    const pairingOn = pairing?.enabled === true;
    const [pairChoice, setPairChoice] = useState<boolean>(readStoredPair);
    const togglePair = useCallback(() => {
        setPairChoice((prev) => { persistStoredPair(!prev); return !prev; });
    }, []);
    // Pick Advisor: the recommendation, and the picker's "Suggested"
    // toggle (ON by default, per device). With it on and a Low-tier
    // recommendation in hand, the queue shows ONLY the recommended tile —
    // or the "ask a leader" panel when nothing qualifies. Off = the
    // normal queue. The High tier is never advised, so it always shows
    // the normal queue.
    const [advisor, setAdvisor] = useState<AdvisorInfo | null>(null);
    const [suggestedOnly, setSuggestedOnly] = useState<boolean>(readSuggestedOnly);
    const toggleSuggested = useCallback(() => {
        setSuggestedOnly((prev) => { persistSuggestedOnly(!prev); return !prev; });
    }, []);
    const [starting, setStarting] = useState(false);
    const [claimingId, setClaimingId] = useState<number | null>(null);
    // Single "busy" flag — covers both a fresh wave-start AND a paused
    // wave claim. Drives the full-page spinner overlay and disables
    // every interactive control on the selector so the picker can't
    // queue a second action while the first is in flight.
    const busy = starting || claimingId !== null;

    // The picker's chosen P&S wave size, clamped to what's actually
    // startable: a size whose pool is empty can't stay selected. When
    // both pools have work either size is a real choice (they're
    // disjoint); with the feature off everything is Small.
    const [waveSizeChoice, setWaveSizeChoice] = useState<"small" | "large">(readStoredWaveSize);
    const persistWaveSize = useCallback((next: "small" | "large") => {
        setWaveSizeChoice(next);
        persistStoredWaveSize(next);
    }, []);
    const waveSize: "small" | "large" = resolveWaveSize(pasLarge, waveSizeChoice);

    // The queue shows the Sorting pool WITHOUT the tote-sized put wall
    // orders; the include choice is made on the chooser page. The ONE
    // exception is the suggested card: when the advisor recommends
    // "Sorting including Putwall orders" the card starts with the include
    // flag on, so its counts must be simulated with the flag on too —
    // otherwise it shows the P&S-only wave (bigger orders, more items)
    // and the wave that opens is the combined one (2026-10-05: 234 items
    // promised, 171 built, picker released and retried four times).
    const pollIncludePutWall = suggestedOnly && tier === "low"
        && advisor?.pick?.pickType === "pick_and_sort"
        && advisor.pick.sortStation === "pick_and_sort"
        && advisor.pick.includePutWall === true;
    const selectorUrl = buildSelectorUrl(filters, waveSize, pollIncludePutWall);

    useEffect(() => {
        if (selectedQueue) return;
        let alive = true;
        const tick = async () => {
            try {
                const res = await apiFetch(selectorUrl, {});
                if (!res.ok || !alive) return;
                const body: SelectorResponse = await res.json();
                if (alive) {
                    setCounts(body.counts ?? { light: 0, pick_and_sort: 0, large: 0 });
                    setLargeOrderCount(typeof body.order_counts?.large === "number" ? body.order_counts.large : null);
                    setPasOrderCount(typeof body.order_counts?.pick_and_sort === "number" ? body.order_counts.pick_and_sort : null);
                    {
                        const pool = body.pool_counts?.pick_and_sort;
                        setPasPool(pool && typeof pool.items === "number" && typeof pool.orders === "number"
                            ? { items: pool.items, orders: pool.orders }
                            : null);
                    }
                    setAgeCounts(body.age_counts ?? { all: 0, "1": 0, "2": 0, "2+": 0 });
                    setDeliveryCounts(body.delivery_counts ?? { all: 0, express: 0, standard: 0 });
                    setDueCount(typeof body.due_count === "number" ? body.due_count : 0);
                    if (body.channel_counts && typeof body.channel_counts === "object") {
                        setChannelCounts(body.channel_counts);
                        if (channelFilter !== "all"
                            && !Object.prototype.hasOwnProperty.call(body.channel_counts, channelFilter)) {
                            setChannelFilter("all");
                        }
                    } else {
                        // Older backends omit this field. Keep the stored selection
                        // untouched so a rolling deployment behaves as before.
                        setChannelCounts({});
                    }
                    setSpecialQueues(Array.isArray(body.special_queues) ? body.special_queues : null);
                    setPaused(Array.isArray(body.paused) ? body.paused : []);
                    setPasLarge(body.pick_and_sort_large ?? null);
                    setPutWall(body.put_wall && body.put_wall.enabled === true ? body.put_wall : null);
                    setAdvisor(body.advisor && typeof body.advisor === "object" ? body.advisor : null);
                    setPairing(body.pairing && typeof body.pairing === "object" ? body.pairing : null);
                }
            } catch { /* transient — next poll retries */ }
        };
        tick();
        const id = window.setInterval(tick, PICKING_POLL_MS);
        return () => { alive = false; window.clearInterval(id); };
    }, [selectorUrl, channelFilter, setChannelFilter, selectedQueue]);

    const claimPaused = useCallback(async (waveId: number) => {
        if (claimingId !== null) return;
        setClaimingId(waveId);
        try {
            const res = await apiFetch(`/picking/wave/${waveId}/resume`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            if (res.ok) {
                onWaveStarted();
                return;
            }
            const errBody = await res.json().catch(() => ({}));
            const code = errBody?.code as string | undefined;
            if (code === "ALREADY_ACTIVE") {
                toast.info(t("picking_resume_already_active"), { toastId: "picking-resume-active" });
                onWaveStarted();
                return;
            }
            if (code === "ALREADY_CLAIMED") {
                toast.info(t("picking_resume_already_claimed"), { toastId: "picking-resume-claimed" });
                try {
                    const r = await apiFetch(selectorUrl, {});
                    if (r.ok) {
                        const body: SelectorResponse = await r.json();
                        setCounts(body.counts ?? counts);
                        setAgeCounts(body.age_counts ?? ageCounts);
                        setDeliveryCounts(body.delivery_counts ?? deliveryCounts);
                        setPaused(Array.isArray(body.paused) ? body.paused : []);
                    }
                } catch { /* let next poll handle it */ }
                return;
            }
            toast.error(t("picking_resume_failed"), { toastId: "picking-resume-fail" });
        } catch {
            toast.error(t("picking_resume_failed"), { toastId: "picking-resume-net" });
        } finally {
            setClaimingId(null);
        }
    }, [claimingId, selectorUrl, counts, ageCounts, deliveryCounts, t, onWaveStarted]);

    // Large is one-order-per-wave, anchored by a location scan: tapping
    // the card opens the anchor prompt INSTEAD of starting anything —
    // the wave is only built (atomically, around the closest eligible
    // order) once the picker scans where they're standing. No wave
    // exists to abandon if they back out.
    const [anchorScan, setAnchorScan] = useState(false);

    // Pick & Sort starts can name a sort station / include choice: the
    // suggested view starts a Put Wall or an "include put wall orders"
    // Sorting wave straight from the queue, where the normal view goes
    // through the chooser page. The chosen key + the recommendation
    // ride along as `advisor` so the wave.start event records whether
    // the picker followed the suggestion.
    const startWave = useCallback(async (
        pickType: PickType,
        opts: { sortStation?: SortStation; includePutWall?: boolean; segments?: 1 | 2 } = {},
    ) => {
        if (starting) return;
        if (pickType === "large") {
            setAnchorScan(true);
            return;
        }
        const sortStation: SortStation = pickType === "pick_and_sort" ? (opts.sortStation ?? "pick_and_sort") : "pick_and_sort";
        const includePutWall = pickType === "pick_and_sort" && sortStation === "pick_and_sort" && opts.includePutWall === true;
        // Paired start: the suggested card's own choice, else the picker's
        // toggle. Only a small Sorting / Putwall wave can be paired; the
        // BE builds solo (and says why) for anything else.
        const segments: 1 | 2 = pickType === "pick_and_sort" && pairingOn && waveSize !== "large"
            && (opts.segments === 2 || (opts.segments === undefined && pairChoice)) ? 2 : 1;
        const chosenKey = pickType === "pick_and_sort"
            ? (sortStation === "put_wall" ? "put_wall" : includePutWall ? "pick_and_sort+put_wall" : "pick_and_sort")
            : pickType;
        const advisorMeta = advisorStartMeta(advisor, chosenKey, suggestedOnly);
        // A suggested pick that will not start (pool taken by another
        // picker, wall too few, build conflict…) must never leave the
        // picker staring at one tile: reveal the full queue on this
        // device and say so. The toggle can be switched back on.
        const fallbackFromSuggested = () => {
            if (!suggestedOnly || !advisorMeta?.followed) return;
            persistSuggestedOnly(false);
            setSuggestedOnly(false);
            toast.info(t("picking_suggested_fallback"), { toastId: "picking-suggested-fallback" });
        };
        setStarting(true);
        // Keep `starting` (and therefore the loading overlay) up
        // through the parent's refetch + render of ActiveWaveScreen.
        // Otherwise the overlay drops the moment /picking/wave/start
        // returns and the picker sees the now-empty selector flash
        // before the scan screen mounts.
        let handedOff = false;
        try {
            // Forward every active filter so the server narrows the
            // candidate pool to exactly what the picker sees. Without
            // this the allocator considers every unfulfilled order in
            // the warehouse — which is fine at demo scale but a
            // performance cliff once unfulfilled grows.
            const res = await apiFetch("/picking/wave/start", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    pickType,
                    tier,
                    ageFilter,
                    deliveryFilter,
                    channelFilter,
                    ageFrom: ageFrom || null,
                    ageTo:   ageTo   || null,
                    dueFilter,
                    // Only meaningful for pick_and_sort; the BE ignores
                    // it for other pick types. A put wall wave is always
                    // one wall, so no size is sent for it.
                    waveSize: pickType === "pick_and_sort" && sortStation !== "put_wall" ? waveSize : undefined,
                    // Station / include fields only when the put wall is a
                    // live choice here (feature on, or the suggested view
                    // named them) — with the feature off the payload is
                    // exactly the pre-put-wall one.
                    ...(pickType === "pick_and_sort" && (putWallOn || opts.sortStation !== undefined || opts.includePutWall !== undefined)
                        ? { sortStation, includePutWall } : {}),
                    ...(segments === 2 ? { segments: 2 } : {}),
                    ...(advisorMeta ? { advisor: advisorMeta } : {}),
                }),
            });
            if (res.ok) {
                handedOff = true;
                // A pair was asked for but the walk never changes aisle:
                // the wave started solo — say so, it is not a failure.
                if (segments === 2) {
                    const okBody = await res.json().catch(() => ({}));
                    if (okBody?.pair_reason === "single_aisle") {
                        toast.info(t("picking_pair_single_aisle"), { toastId: "picking-pair-single-aisle" });
                    } else if (okBody?.pair_reason === "too_small") {
                        toast.info(t("picking_pair_too_small", { units: String(pairing?.min_units ?? 100) }), { toastId: "picking-pair-too-small" });
                    }
                }
                onWaveStarted();
                return;
            }
            const errBody = await res.json().catch(() => ({}));
            const code = errBody?.code as string | undefined;
            if (code === "ALREADY_ACTIVE") {
                handedOff = true;
                toast.info(t("picking_already_active_wave"), { toastId: "picking-already-active" });
                onWaveStarted();
                return;
            }
            if (code === "NO_CANDIDATES" || code === "NO_MATCHING" || code === "NO_SELECTED") {
                toast.info(t("picking_no_orders_to_pick"), { toastId: "picking-no-orders" });
                fallbackFromSuggested();
                return;
            }
            if (code === "BUILD_CONFLICT") {
                // A prior wave left a conflicting artefact (e.g. a live
                // allocation). NOT an active wave — so DON'T hand off /
                // keep the loading overlay up; surface an actionable
                // message and let the picker retry after a release.
                toast.error(t("picking_start_conflict"), { toastId: "picking-start-conflict" });
                fallbackFromSuggested();
                return;
            }
            // Large-first policy races: the queue crossed the small-cap
            // threshold between the last poll and the start. The next
            // poll refreshes the enforced size; tell the picker which
            // size to use.
            if (code === "PS_SMALL_BLOCKED") {
                toast.info(t("picking_small_blocked"), { toastId: "picking-small-blocked" });
                fallbackFromSuggested();
                return;
            }
            if (code === "PS_LARGE_TOO_FEW") {
                toast.info(t("picking_large_too_few"), { toastId: "picking-large-too-few" });
                fallbackFromSuggested();
                return;
            }
            if (code === "PS_LARGE_UNAVAILABLE") {
                toast.info(t("picking_large_wave_unavailable"), { toastId: "picking-large-unavailable" });
                fallbackFromSuggested();
                return;
            }
            if (code === "PW_UNAVAILABLE" || code === "PW_TOO_FEW") {
                toast.info(t("picking_no_orders_to_pick"), { toastId: "picking-put-wall-unavailable" });
                fallbackFromSuggested();
                return;
            }
            toast.error(t("picking_start_failed"), { toastId: "picking-start-fail" });
            fallbackFromSuggested();
        } catch {
            toast.error(t("picking_start_failed"), { toastId: "picking-start-net" });
        } finally {
            if (!handedOff) {
                setStarting(false);
            }
        }
    }, [starting, tier, ageFilter, deliveryFilter, channelFilter, ageFrom, ageTo, dueFilter, waveSize, onWaveStarted, t, advisor, suggestedOnly, pairingOn, pairChoice, pairing]);

    // Featured-card tap with the put wall on: go to the "Which wave?" page
    // (Put Wall vs Sorting) instead of starting. It gets the live filter
    // set so its counts match what the picker is looking at here.
    const openChooser = useCallback(() => {
        navigate("/picking/choose", { state: filters });
    }, [navigate, filters]);

    const allEmpty = useMemo(
        () => counts.light === 0 && counts.pick_and_sort === 0 && counts.large === 0
            && (putWall?.orders ?? 0) === 0,
        [counts, putWall],
    );

    const hasSpecialQueueOrders = specialQueues?.some(queue => queue.orders > 0) ?? false;

    const renderFeatured = () => {
        const count = counts.pick_and_sort;
        // With the put wall on, the card is also the way INTO the put wall
        // choice — so it stays tappable while only the tote-sized pool has
        // work, even if the exclusive Sorting count reads 0.
        const hasWork = count > 0 || (putWallOn && putWall.orders > 0);
        const usable = !busy && hasWork;
        const disabled = !hasWork;
        return (
            <button
                type="button"
                disabled={!usable}
                className={`picking-card picking-card--featured${disabled ? " picking-card--disabled" : ""}`}
                onClick={() => usable && (putWallOn ? openChooser() : startWave("pick_and_sort"))}
            >
                <div className="picking-card-featured-top">
                    <div className="picking-card-icon">{iconFor("pick_and_sort", 20)}</div>
                    <div className="picking-card-suggested">{t("picking_card_suggested")}</div>
                </div>
                <div className="picking-card-featured-bottom">
                    <div className="picking-card-featured-text">
                        <div className="picking-card-featured-name">{t("picking_pick_type_pick_and_sort")}</div>
                        {pasPool !== null && (
                            // Whole ready pool under the active filters — the
                            // next wave (right-hand numbers) is drawn from it.
                            <div className="picking-card-featured-pool" data-testid="pas-pool">
                                <span className="picking-card-featured-pool-label">{t("picking_card_ready")}</span>
                                <span className="picking-card-featured-pool-row">
                                    <span className="picking-card-featured-pool-num">{pasPool.items}</span>
                                    <span className="picking-card-featured-pool-unit">{t("picking_card_items")}</span>
                                </span>
                                <span className="picking-card-featured-pool-sep" aria-hidden="true">·</span>
                                <span className="picking-card-featured-pool-row">
                                    <span className="picking-card-featured-pool-num">{pasPool.orders}</span>
                                    <span className="picking-card-featured-pool-unit">{t("picking_card_orders")}</span>
                                </span>
                            </div>
                        )}
                        {putWallOn && (
                            // The tote-sized pool the chooser's Put Wall
                            // choice draws from — held out of the numbers
                            // on the right, so it's named here.
                            <div className="picking-card-featured-pool" data-testid="put-wall-pool">
                                <span className="picking-card-featured-pool-label">{t("picking_wave_chooser_put_wall")}:</span>
                                <span className="picking-card-featured-pool-row">
                                    <span className="picking-card-featured-pool-num">{putWall.orders}</span>
                                    <span className="picking-card-featured-pool-unit">{t("picking_card_orders")}</span>
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="picking-card-featured-nums">
                        {pasOrderCount !== null && (
                            <div className="picking-card-featured-caption">{t("picking_card_next_wave")}</div>
                        )}
                        <div className="picking-card-featured-num">
                            {count}
                            <span className="picking-card-featured-items">{t("picking_card_items")}</span>
                        </div>
                        {pasOrderCount !== null && (
                            <div className="picking-card-featured-num picking-card-featured-num--orders" data-testid="pas-orders">
                                {pasOrderCount}
                                <span className="picking-card-featured-items">{t("picking_card_orders")}</span>
                            </div>
                        )}
                    </div>
                </div>
            </button>
        );
    };

    const renderStandard = (kind: "light" | "large") => {
        const count = counts[kind];
        const usable = !busy && count > 0;
        const disabled = count === 0;
        return (
            <button
                type="button"
                disabled={!usable}
                className={`picking-card picking-card--standard picking-card--${kind}${disabled ? " picking-card--disabled" : ""}`}
                onClick={() => usable && startWave(kind)}
            >
                <div className="picking-card-head">
                    <div className="picking-card-icon">{iconFor(kind, 20)}</div>
                </div>
                <div className="picking-card-bottom">
                    {kind === "large" && largeOrderCount !== null ? (
                        // Large is one order per wave — the tile shows the
                        // WHOLE filtered pool: items and orders as separate
                        // stacked rows (number + label per row, so a large
                        // count can never run into its label).
                        <div className="picking-card-pool">
                            <div className="picking-card-pool-row">
                                <span className="picking-card-pool-num">{count}</span>
                                <span className="picking-card-items">{t("picking_card_items")}</span>
                            </div>
                            <div className="picking-card-pool-row">
                                <span className="picking-card-pool-num">{largeOrderCount}</span>
                                <span className="picking-card-items">{t("picking_card_orders")}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="picking-card-num">
                            {count}
                            <span className="picking-card-items">{t("picking_card_items")}</span>
                        </div>
                    )}
                    <div className="picking-card-name">{t(`picking_pick_type_${kind}`)}</div>
                </div>
                <div className="picking-card-watermark" aria-hidden="true">
                    {iconFor(kind, 90)}
                </div>
            </button>
        );
    };

    if (anchorScan) {
        return (
            <LargeAnchorScan
                prompt={t("picking_sub_scan_starting_location")}
                exitLabel={t("picking_anchor_back")}
                onExit={() => setAnchorScan(false)}
                onScan={async (barcode) => {
                    const outcome = await startLargeWaveAt(barcode, {
                        tier, ageFilter, deliveryFilter, channelFilter,
                        ageFrom: ageFrom || null,
                        ageTo: ageTo || null,
                        dueFilter,
                        advisor: advisorStartMeta(advisor, "large", suggestedOnly),
                    }, t);
                    if (outcome.kind === "started") onWaveStarted();
                    return outcome;
                }}
            />
        );
    }

    if (selectedQueue) {
        return (
            <QueueOrderList
                queue={selectedQueue}
                startOptions={{ tier, ageFilter, deliveryFilter, channelFilter, ageFrom, ageTo, dueFilter }}
                onBack={() => setSelectedQueue(null)}
                onWaveStarted={onWaveStarted}
            />
        );
    }

    // ── Suggested view (Pick Advisor) ─────────────────────────────────
    // Active when the toggle is on, the tier is Low and the BE sent a
    // recommendation (or an explicit "nothing qualifies"). Shows ONE
    // tile and nothing else to choose from; the reason is admin-only.
    const suggestedActive = suggestedOnly && tier === "low" && advisor !== null
        && (advisor.pick !== null || advisor.rule === "ask_leader");
    const renderSuggested = () => {
        const pick = advisor?.pick ?? null;
        if (!pick) {
            return (
                <div className="picking-ask-leader" role="status" data-testid="picking-ask-leader">
                    <Sparkles size={28} aria-hidden="true" />
                    <div className="picking-ask-leader-title">{t("picking_suggested_none_title")}</div>
                    <div className="picking-ask-leader-body">{t("picking_suggested_none_body")}</div>
                    <button type="button" className="btn btn-ghost dispatch-modal-btn" onClick={toggleSuggested} disabled={busy}>
                        {t("picking_suggested_show_all")}
                    </button>
                </div>
            );
        }
        const isWall = pick.pickType === "pick_and_sort" && pick.sortStation === "put_wall";
        // Paired waves: a JOIN recommendation picks up the open half of a
        // wave someone else started — through the resume endpoint, like
        // the Join card. Rendered as its own tile, with no counts to show.
        const joinId = typeof pick.join === "number" ? pick.join : null;
        if (joinId !== null) {
            const joinWave = paused.find((w) => Number(w.wave_id) === joinId);
            const startedBy = joinWave?.sibling_picker_name ?? (typeof advisor?.reason?.started_by === "string" ? advisor.reason.started_by : null);
            return (
                <button
                    type="button"
                    disabled={busy}
                    className="picking-card picking-card--featured"
                    onClick={() => { if (!busy) claimPaused(joinId); }}
                    data-testid="picking-suggested-card"
                >
                    <div className="picking-card-featured-top">
                        <div className="picking-card-icon"><Users size={20} strokeWidth={1.8} /></div>
                        <div className="picking-card-suggested">{t("picking_suggested_badge")}</div>
                    </div>
                    <div className="picking-card-featured-bottom">
                        <div className="picking-card-featured-text">
                            <div className="picking-card-featured-name">
                                {t("picking_join_title", { type: isWall ? t("picking_wave_chooser_put_wall") : t("picking_pick_type_pick_and_sort") })}
                            </div>
                            <div className="picking-card-suggested-note">
                                {t("picking_segment_badge", { no: String(pick.segment_no ?? 2), of: "2" })}
                                {startedBy ? ` · ${t("picking_join_started_by_short", { name: startedBy })}` : ""}
                            </div>
                        </div>
                        <div className="picking-card-featured-nums">
                            <div className="picking-card-featured-num">
                                {joinWave?.lines_total ?? "—"}
                                <span className="picking-card-featured-items">{t("picking_join_lines")}</span>
                            </div>
                            <div className="picking-card-featured-num picking-card-featured-num--orders">
                                {joinWave?.items_total ?? pick.orders}
                                <span className="picking-card-featured-items">{joinWave ? t("picking_card_items") : t("picking_card_orders")}</span>
                            </div>
                        </div>
                    </div>
                </button>
            );
        }
        const name = isWall
            ? t("picking_wave_chooser_put_wall")
            : t(`picking_pick_type_${pick.pickType}`);
        const items = isWall ? (putWall?.next_items ?? 0) : counts[pick.pickType];
        const orders = isWall ? (putWall?.next_orders ?? pick.orders) : pick.pickType === "pick_and_sort" ? (pasOrderCount ?? pick.orders) : pick.pickType === "large" ? (largeOrderCount ?? pick.orders) : pick.orders;
        const hasWork = isWall ? (putWall?.orders ?? 0) > 0 : pick.pickType === "pick_and_sort" ? (counts.pick_and_sort > 0 || (pick.includePutWall && (putWall?.orders ?? 0) > 0)) : counts[pick.pickType] > 0;
        const usable = !busy && hasWork;
        const paired = pick.pickType === "pick_and_sort" && pick.segments === 2 && pairingOn;
        const onStart = () => {
            if (!usable) return;
            if (pick.pickType === "pick_and_sort") startWave("pick_and_sort", { sortStation: pick.sortStation, includePutWall: pick.includePutWall, segments: paired ? 2 : 1 });
            else startWave(pick.pickType);
        };
        return (
            <button
                type="button"
                disabled={!usable}
                className={`picking-card picking-card--featured${hasWork ? "" : " picking-card--disabled"}`}
                onClick={onStart}
                data-testid="picking-suggested-card"
            >
                <div className="picking-card-featured-top">
                    <div className="picking-card-icon">{isWall ? <Grid3x3 size={20} strokeWidth={1.8} /> : iconFor(pick.pickType, 20)}</div>
                    <div className="picking-card-suggested">{t("picking_suggested_badge")}</div>
                </div>
                <div className="picking-card-featured-bottom">
                    <div className="picking-card-featured-text">
                        <div className="picking-card-featured-name">{name}</div>
                        {pick.includePutWall && !isWall && (
                            <div className="picking-card-suggested-note">{t("picking_suggested_includes_put_wall")}</div>
                        )}
                        {paired && (
                            <div className="picking-card-suggested-note" data-testid="picking-suggested-pair">{t("picking_suggested_as_pair")}</div>
                        )}
                    </div>
                    <div className="picking-card-featured-nums">
                        <div className="picking-card-featured-caption">{t("picking_card_next_wave")}</div>
                        <div className="picking-card-featured-num">
                            {items}
                            <span className="picking-card-featured-items">{t("picking_card_items")}</span>
                        </div>
                        <div className="picking-card-featured-num picking-card-featured-num--orders">
                            {orders}
                            <span className="picking-card-featured-items">{t("picking_card_orders")}</span>
                        </div>
                    </div>
                </div>
            </button>
        );
    };

    return (
        <div className="picking-selector">
            <div className="page-title-bar picking-selector-header">
                <h1 className="page-title">{t("pick_queue")}</h1>
                <TierToggle tier={tier} onChange={f.setTier} busy={busy} />
                <button
                    type="button"
                    className="picking-suggested-toggle"
                    aria-pressed={suggestedOnly}
                    onClick={toggleSuggested}
                    disabled={busy}
                    title={t("picking_suggested_toggle_hint")}
                    aria-label={t("picking_suggested_toggle")}
                    data-testid="picking-suggested-toggle"
                >
                    <Sparkles size={18} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="page-back-btn"
                    onClick={() => navigate("/pick-face")}
                    disabled={busy}
                    aria-label="Back to Pick Face"
                >
                    <ArrowLeft size={20} />
                </button>
            </div>

            <SelectorFilterRows f={f} counts={{ ageCounts, deliveryCounts, channelCounts, dueCount }} busy={busy} />

            {suggestedActive && (
                <div className="picking-card-grid picking-card-grid--suggested">
                    {renderSuggested()}
                </div>
            )}

            {/* With the put wall on, the Small/Large pills move into the
                chooser's Sorting choice — they only apply to a bay wave. */}
            {!suggestedActive && pasLarge?.available && !putWallOn && (
                <div className="picking-wave-size-row" role="radiogroup" aria-label={t("picking_wave_size")}>
                    <span className="picking-wave-size-label">{t("picking_wave_size")}</span>
                    <button
                        type="button"
                        role="radio"
                        aria-checked={waveSize === "small"}
                        className={`picking-wave-size-pill${waveSize === "small" ? " picking-wave-size-pill--active" : ""}`}
                        disabled={busy || !pasLarge.small_ready}
                        onClick={() => persistWaveSize("small")}
                        title={pasLarge.small_ready ? t("picking_wave_size_small") : t("picking_small_blocked")}
                    >
                        {t("picking_wave_size_small")} · {pasLarge.small_cap}
                    </button>
                    <button
                        type="button"
                        role="radio"
                        aria-checked={waveSize === "large"}
                        className={`picking-wave-size-pill${waveSize === "large" ? " picking-wave-size-pill--active" : ""}`}
                        disabled={busy || !pasLarge.large_ready}
                        onClick={() => persistWaveSize("large")}
                        title={pasLarge.large_ready ? t("picking_wave_size_large") : t("picking_large_too_few")}
                    >
                        {t("picking_wave_size_large")} · {pasLarge.max_orders ?? ""}
                    </button>
                </div>
            )}

            {/* Paired waves (Alex, 2026-10-16): two pickers on one Sorting /
                Putwall wave. The toggle only exists where the warehouse
                setting is on; a Large (two-bay) wave is never paired. */}
            {!suggestedActive && pairingOn && waveSize !== "large" && (
                <div className="picking-pair-row">
                    <button
                        type="button"
                        className="picking-pair-toggle"
                        aria-pressed={pairChoice}
                        onClick={togglePair}
                        disabled={busy}
                        title={t("picking_pair_toggle_hint")}
                        data-testid="picking-pair-toggle"
                    >
                        <Users size={16} aria-hidden="true" />
                        {t("picking_pair_toggle")}
                    </button>
                </div>
            )}

            {!suggestedActive && (
                <div className="picking-card-grid">
                    {renderFeatured()}
                    {renderStandard("light")}
                    {renderStandard("large")}
                </div>
            )}

            {/* Special queues and paused waves are UNFINISHED or leader-
                assigned work, not new-wave recommendations: they stay
                visible in the suggested view so nothing sits hidden
                behind the toggle (stuck-order review, 2026-10-02). */}
            <SpecialQueuesSection
                queues={specialQueues}
                busy={busy}
                onOpenQueue={setSelectedQueue}
            />

            {!suggestedActive && allEmpty && !hasSpecialQueueOrders && paused.length === 0 && (
                <div className="picking-selector-empty">{t("picking_no_orders_to_pick")}</div>
            )}

            <PausedWavesList waves={paused} onClaim={claimPaused} claiming={claimingId} busy={busy} />

            {/* Shown for every pick type while we wait for the BE
                allocation + the parent's refetch + ActiveWaveScreen
                mount. Locks the page so the picker can't tap a second
                card mid-allocation. */}
            {busy && <WaveStartingOverlay />}
        </div>
    );
};

export default PickTypeSelector;
